import type { Metadata } from "next";
import { SERVICE_PAGES } from "@/lib/servicePages";
import { SOLUTIONS, SOLUTIONS_HUB_META } from "@/lib/solutions";
import type { DeckDefinition } from "@/lib/deck/content";
import { ALL_DECKS } from "@/lib/deck/decks";
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_TITLE,
  defaultSiteMetadata,
  resolveMetadata,
  withDefaultOgImage,
  type ResolvedPageMetadata,
} from "@/lib/siteMetadata";

const STATIC_ROUTES: Record<string, Metadata> = {
  "/": withDefaultOgImage({
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    alternates: { canonical: "https://doimpo.com" },
    openGraph: {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
      url: "https://doimpo.com",
      siteName: "doimpo",
      type: "website",
    },
    twitter: { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION },
  }),
  "/about": withDefaultOgImage({
    title: "About doimpo - Do Important. Do Impossible.",
    description:
      "doimpo is the growth partner for founders tired of juggling vendors—strategy, design, development, and marketing under one roof. Founded by David Enoch.",
    alternates: { canonical: "https://doimpo.com/about" },
    openGraph: {
      title: "About doimpo - Do Important. Do Impossible.",
      description:
        "doimpo is the growth partner for founders tired of juggling vendors—strategy, design, development, and marketing under one roof. Founded by David Enoch.",
      url: "https://doimpo.com/about",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "About doimpo - Do Important. Do Impossible.",
      description:
        "doimpo is the growth partner for founders tired of juggling vendors—strategy, design, development, and marketing under one roof. Founded by David Enoch.",
    },
  }),
  "/contact": withDefaultOgImage({
    title: "Contact | doimpo - Let's Talk",
    description:
      "Start the conversation. Email hello@doimpo.com or book a call—we work across US, UK, AU and UAE time zones and reply within one business day.",
    alternates: { canonical: "https://doimpo.com/contact" },
    openGraph: {
      title: "Contact | doimpo - Let's Talk",
      description:
        "Start the conversation. Email hello@doimpo.com or book a call—we work across US, UK, AU and UAE time zones and reply within one business day.",
      url: "https://doimpo.com/contact",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Contact | doimpo - Let's Talk",
      description:
        "Start the conversation. Email hello@doimpo.com or book a call—we work across US, UK, AU and UAE time zones and reply within one business day.",
    },
  }),
  "/collab": withDefaultOgImage({
    title: "The Collab Multiverse | doimpo",
    description:
      "doimpo collab, studio, digital, and code—four divisions, one brief for brand, product, and growth.",
    alternates: { canonical: "https://doimpo.com/collab" },
    openGraph: {
      title: "The Collab Multiverse | doimpo",
      description:
        "doimpo collab, studio, digital, and code—four divisions, one brief for brand, product, and growth.",
      url: "https://doimpo.com/collab",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "The Collab Multiverse | doimpo",
      description:
        "doimpo collab, studio, digital, and code—four divisions, one brief for brand, product, and growth.",
    },
  }),
  "/services": withDefaultOgImage({
    title: "Services | doimpo — Strategy, Brand, Product, Growth",
    description:
      "Eight services. One partner. Strategy, branding, development, marketing, AI, content, and commerce under one roof.",
    alternates: { canonical: "https://doimpo.com/services" },
    openGraph: {
      title: "Services | doimpo — Strategy, Brand, Product, Growth",
      description:
        "Eight services. One partner. Strategy, branding, development, marketing, AI, content, and commerce under one roof.",
      url: "https://doimpo.com/services",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Services | doimpo — Strategy, Brand, Product, Growth",
      description:
        "Eight services. One partner. Strategy, branding, development, marketing, AI, content, and commerce under one roof.",
    },
  }),
  "/blueprint": withDefaultOgImage({
    title: "Free Growth Blueprint | doimpo",
    description:
      "Free 90-day growth plan for your stage—on screen in about three minutes. No credit card. No pitch deck required.",
    alternates: { canonical: "https://doimpo.com/blueprint" },
    openGraph: {
      title: "Free Growth Blueprint | doimpo",
      description:
        "Free 90-day growth plan for your stage—on screen in about three minutes. No credit card. No pitch deck required.",
      url: "https://doimpo.com/blueprint",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Free Growth Blueprint | doimpo",
      description:
        "Free 90-day growth plan for your stage—on screen in about three minutes. No credit card. No pitch deck required.",
    },
  }),
  "/reign-conference": withDefaultOgImage({
    title: "Reign 2026 | Purpose-to-Profit Diagnostic | doimpo",
    description:
      "Free AI-driven diagnostic at Reign 2026. Grade your digital presence on a 0–100 Purpose-to-Profit Scale in 60 seconds.",
    alternates: { canonical: "https://doimpo.com/reign-conference" },
    openGraph: {
      title: "Reign 2026 | Purpose-to-Profit Diagnostic | doimpo",
      description:
        "Free AI-driven diagnostic at Reign 2026. Grade your digital presence on a 0–100 Purpose-to-Profit Scale in 60 seconds.",
      url: "https://doimpo.com/reign-conference",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Reign 2026 | Purpose-to-Profit Diagnostic | doimpo",
      description:
        "Free AI-driven diagnostic at Reign 2026. Grade your digital presence on a 0–100 Purpose-to-Profit Scale in 60 seconds.",
    },
  }),
  "/privacy": withDefaultOgImage({
    title: "Privacy Policy | doimpo",
    description:
      "How doimpo collects, uses, and protects information when you use doimpo.com and contact us.",
    alternates: { canonical: "https://doimpo.com/privacy" },
    openGraph: {
      title: "Privacy Policy | doimpo",
      description:
        "How doimpo collects, uses, and protects information when you use doimpo.com and contact us.",
      url: "https://doimpo.com/privacy",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Privacy Policy | doimpo",
      description:
        "How doimpo collects, uses, and protects information when you use doimpo.com and contact us.",
    },
  }),
  "/terms": withDefaultOgImage({
    title: "Terms of Use | doimpo",
    description:
      "Terms governing use of doimpo.com and doimpo digital services.",
    alternates: { canonical: "https://doimpo.com/terms" },
    openGraph: {
      title: "Terms of Use | doimpo",
      description:
        "Terms governing use of doimpo.com and doimpo digital services.",
      url: "https://doimpo.com/terms",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Terms of Use | doimpo",
      description:
        "Terms governing use of doimpo.com and doimpo digital services.",
    },
  }),
  "/scan": withDefaultOgImage({
    title: "Diagnostic Digital Presence Audit | doimpo",
    description:
      "Request a free diagnostic digital presence audit. We analyze your website, branding, search visibility, and performance, delivering tailored insights within a week.",
    alternates: { canonical: "https://doimpo.com/scan" },
    openGraph: {
      title: "Diagnostic Digital Presence Audit | doimpo",
      description:
        "Request a free diagnostic digital presence audit. We analyze your website, branding, search visibility, and performance, delivering tailored insights within a week.",
      url: "https://doimpo.com/scan",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Diagnostic Digital Presence Audit | doimpo",
      description:
        "Request a free diagnostic digital presence audit. We analyze your website, branding, search visibility, and performance, delivering tailored insights within a week.",
    },
  }),
  "/clinics": withDefaultOgImage({
    title: "Audit Your Clinic's Digital Workflow in 90 Seconds | doimpo",
    description:
      "For aesthetic clinic owners and medical directors: a voice-first diagnostic of your booking, content and follow-up workflow. Four taps, one voice note, then a consult or a free automation guide.",
    alternates: { canonical: "https://doimpo.com/clinics" },
    openGraph: {
      title: "Audit Your Clinic's Digital Workflow in 90 Seconds | doimpo",
      description:
        "Voice-first diagnostic for aesthetic clinics: where your bookings, before & afters and follow-ups leak — and what one connected system looks like.",
      url: "https://doimpo.com/clinics",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Audit Your Clinic's Digital Workflow in 90 Seconds | doimpo",
      description:
        "Voice-first diagnostic for aesthetic clinics: where your bookings, before & afters and follow-ups leak — and what one connected system looks like.",
    },
  }),
  "/book": withDefaultOgImage({
    title: "Book a Call | doimpo",
    description:
      "Schedule a strategy call with doimpo. We work across US, UK, Australian and UAE time zones—pick a slot that suits you.",
    alternates: { canonical: "https://doimpo.com/book" },
    openGraph: {
      title: "Book a Call | doimpo",
      description:
        "Schedule a strategy call with doimpo. We work across US, UK, Australian and UAE time zones—pick a slot that suits you.",
      url: "https://doimpo.com/book",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Book a Call | doimpo",
      description:
        "Schedule a strategy call with doimpo. We work across US, UK, Australian and UAE time zones—pick a slot that suits you.",
    },
  }),
  "/us": withDefaultOgImage({
    title: "Digital Growth Agency for US Businesses | doimpo",
    description:
      "Full-service digital agency for US startups and SMBs—strategy, branding, web development, and marketing under one roof. Book a call in your timezone.",
    alternates: { canonical: "https://doimpo.com/us" },
    openGraph: {
      title: "Digital Growth Agency for US Businesses | doimpo",
      description:
        "Full-service digital agency for US startups and SMBs—strategy, branding, web development, and marketing under one roof. Book a call in your timezone.",
      url: "https://doimpo.com/us",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Digital Growth Agency for US Businesses | doimpo",
      description:
        "Full-service digital agency for US startups and SMBs—strategy, branding, web development, and marketing under one roof. Book a call in your timezone.",
    },
  }),
  "/uk": withDefaultOgImage({
    title: "Digital Growth Agency for UK Businesses | doimpo",
    description:
      "Strategy, branding, web and marketing for UK companies—one unified growth partner. Book a call across UK business hours.",
    alternates: { canonical: "https://doimpo.com/uk" },
    openGraph: {
      title: "Digital Growth Agency for UK Businesses | doimpo",
      description:
        "Strategy, branding, web and marketing for UK companies—one unified growth partner. Book a call across UK business hours.",
      url: "https://doimpo.com/uk",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Digital Growth Agency for UK Businesses | doimpo",
      description:
        "Strategy, branding, web and marketing for UK companies—one unified growth partner. Book a call across UK business hours.",
    },
  }),
  "/au": withDefaultOgImage({
    title: "Digital Growth Agency for Australian Businesses | doimpo",
    description:
      "Web development, branding, and digital marketing for Australian SMBs—one partner, clear playbook. Book a call in AEST/AEDT.",
    alternates: { canonical: "https://doimpo.com/au" },
    openGraph: {
      title: "Digital Growth Agency for Australian Businesses | doimpo",
      description:
        "Web development, branding, and digital marketing for Australian SMBs—one partner, clear playbook. Book a call in AEST/AEDT.",
      url: "https://doimpo.com/au",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Digital Growth Agency for Australian Businesses | doimpo",
      description:
        "Web development, branding, and digital marketing for Australian SMBs—one partner, clear playbook. Book a call in AEST/AEDT.",
    },
  }),
  "/uae": withDefaultOgImage({
    title: "Digital Growth Agency for UAE Businesses | doimpo",
    description:
      "Strategy, branding, web and marketing for UAE businesses in Dubai, Abu Dhabi and beyond—one growth partner, with Gulf Standard Time overlap.",
    alternates: { canonical: "https://doimpo.com/uae" },
    openGraph: {
      title: "Digital Growth Agency for UAE Businesses | doimpo",
      description:
        "Strategy, branding, web and marketing for UAE businesses in Dubai, Abu Dhabi and beyond—one growth partner, with Gulf Standard Time overlap.",
      url: "https://doimpo.com/uae",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Digital Growth Agency for UAE Businesses | doimpo",
      description:
        "Strategy, branding, web and marketing for UAE businesses in Dubai, Abu Dhabi and beyond—one growth partner, with Gulf Standard Time overlap.",
    },
  }),
  "/blog": withDefaultOgImage({
    title: "Blog | doimpo - Growth Insights",
    description:
      "Strategy, branding, marketing, and AI insights for founders building in the US, UK, Australia, the UAE, and beyond.",
    alternates: { canonical: "https://doimpo.com/blog" },
    openGraph: {
      title: "Blog | doimpo - Growth Insights",
      description:
        "Strategy, branding, marketing, and AI insights for founders building in the US, UK, Australia, the UAE, and beyond.",
      url: "https://doimpo.com/blog",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Blog | doimpo - Growth Insights",
      description:
        "Strategy, branding, marketing, and AI insights for founders building in the US, UK, Australia, the UAE, and beyond.",
    },
  }),
  "/guides": withDefaultOgImage({
    title: "Guides | doimpo",
    description:
      "Practical playbooks for launching websites, building brand identity, and running 90-day marketing plans.",
    alternates: { canonical: "https://doimpo.com/guides" },
    openGraph: {
      title: "Guides | doimpo",
      description:
        "Practical playbooks for launching websites, building brand identity, and running 90-day marketing plans.",
      url: "https://doimpo.com/guides",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Guides | doimpo",
      description:
        "Practical playbooks for launching websites, building brand identity, and running 90-day marketing plans.",
    },
  }),
  "/resources": withDefaultOgImage({
    title: "Resources | doimpo",
    description:
      "Checklists and frameworks for messaging, channels, Core Web Vitals, and pre-launch readiness.",
    alternates: { canonical: "https://doimpo.com/resources" },
    openGraph: {
      title: "Resources | doimpo",
      description:
        "Checklists and frameworks for messaging, channels, Core Web Vitals, and pre-launch readiness.",
      url: "https://doimpo.com/resources",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Resources | doimpo",
      description:
        "Checklists and frameworks for messaging, channels, Core Web Vitals, and pre-launch readiness.",
    },
  }),
  "/portfolio": withDefaultOgImage({
    title: "Work | doimpo Portfolio",
    description:
      "Brand, product, and growth case studies for founders who wanted one accountable partner.",
    alternates: { canonical: "https://doimpo.com/portfolio" },
    openGraph: {
      title: "Work | doimpo Portfolio",
      description:
        "Brand, product, and growth case studies for founders who wanted one accountable partner.",
      url: "https://doimpo.com/portfolio",
      siteName: "doimpo",
      type: "website",
    },
    twitter: {
      title: "Work | doimpo Portfolio",
      description:
        "Brand, product, and growth case studies for founders who wanted one accountable partner.",
    },
  }),
};

function deckMetadata(deck: DeckDefinition): Metadata {
  return withDefaultOgImage({
    title: deck.title,
    description: deck.description,
    alternates: { canonical: deck.url },
    openGraph: {
      title: deck.title,
      description: deck.description,
      url: deck.url,
      siteName: "doimpo",
      type: "website",
      images: [{ url: deck.ogImage, width: 1200, height: 630, alt: deck.ogImageAlt, type: "image/jpeg" }],
    },
    twitter: { title: deck.title, description: deck.description, images: [deck.ogImage] },
  });
}

for (const deck of ALL_DECKS) {
  STATIC_ROUTES[deck.path] = deckMetadata(deck);
}

for (const page of Object.values(SERVICE_PAGES)) {
  STATIC_ROUTES[`/services/${page.slug}`] = withDefaultOgImage(page.metadata);
}

STATIC_ROUTES["/solutions"] = withDefaultOgImage({
  title: SOLUTIONS_HUB_META.title,
  description: SOLUTIONS_HUB_META.description,
  alternates: { canonical: SOLUTIONS_HUB_META.url },
  openGraph: {
    title: SOLUTIONS_HUB_META.title,
    description: SOLUTIONS_HUB_META.description,
    url: SOLUTIONS_HUB_META.url,
    siteName: "doimpo",
    type: "website",
    images: [{ url: SOLUTIONS_HUB_META.ogImage, width: 1200, height: 630, alt: SOLUTIONS_HUB_META.ogImageAlt, type: "image/jpeg" }],
  },
  twitter: { title: SOLUTIONS_HUB_META.title, description: SOLUTIONS_HUB_META.description, images: [SOLUTIONS_HUB_META.ogImage] },
});

for (const sol of SOLUTIONS) {
  const solUrl = `https://doimpo.com/solutions/${sol.slug}`;
  STATIC_ROUTES[`/solutions/${sol.slug}`] = withDefaultOgImage({
    title: sol.seoTitle,
    description: sol.seoDescription,
    alternates: { canonical: solUrl },
    openGraph: {
      title: sol.seoTitle,
      description: sol.seoDescription,
      url: solUrl,
      siteName: "doimpo",
      type: "website",
      images: [{ url: sol.ogImage, width: 1200, height: 630, alt: `${sol.label} - doimpo`, type: "image/jpeg" }],
    },
    twitter: { title: sol.seoTitle, description: sol.seoDescription, images: [sol.ogImage] },
  });
}

export function getPageMetadata(pathname: string): ResolvedPageMetadata {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const metadata = STATIC_ROUTES[normalized] ?? defaultSiteMetadata;
  return resolveMetadata(metadata, normalized);
}
